import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const AGENCY_JWT_KEY = 'agency_jwt';
const getAgencyToken = () => localStorage.getItem(AGENCY_JWT_KEY) || '';

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`color: white;`;

const Title = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0 0 28px;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Section = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const MemberTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255,255,255,0.3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 0 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const Td = styled.td`
  padding: 12px 0;
  font-size: 0.88rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.8);
`;

const RoleBadge = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  background: ${p => p.$owner
    ? 'rgba(255,71,87,0.15)'
    : 'rgba(255,255,255,0.07)'};
  color: ${p => p.$owner ? '#ff7c89' : 'rgba(255,255,255,0.45)'};
  border: 1px solid ${p => p.$owner ? 'rgba(255,71,87,0.3)' : 'rgba(255,255,255,0.12)'};
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => p.$active ? '#2ecc71' : 'rgba(255,255,255,0.2)'};
  margin-right: 6px;
`;

const InviteRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const EmailInput = styled.input`
  flex: 1;
  min-width: 200px;
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  padding: 10px 14px;
  outline: none;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const GenBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 10px 20px;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { background: #e0404f; }
  &:disabled { background: #333; color: #555; cursor: default; }
`;

const LinkBox = styled.div`
  margin-top: 14px;
  background: #0c0c1c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LinkText = styled.code`
  flex: 1;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.55);
  word-break: break-all;
  font-family: 'SF Mono', 'Fira Mono', monospace;
`;

const CopyBtn = styled.button`
  background: rgba(255,255,255,0.07);
  color: rgba(255,255,255,0.7);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.12); color: white; }
`;

const Expiry = styled.p`
  font-size: 0.75rem;
  color: rgba(255,255,255,0.25);
  margin: 8px 0 0;
`;

const ErrMsg = styled.p`color: #ff6b7a; font-size: 0.82rem; margin: 10px 0 0;`;
const OkMsg = styled.p`color: #2ecc71; font-size: 0.82rem; margin: 10px 0 0;`;
const Empty = styled.p`color: rgba(255,255,255,0.25); font-size: 0.88rem; margin: 0;`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const HistoryTh = styled.th`
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255,255,255,0.3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 12px 10px 0;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const HistoryTd = styled.td`
  padding: 10px 12px 10px 0;
  font-size: 0.85rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.7);
`;

const IpText = styled.span`
  font-family: 'SF Mono', 'Fira Mono', monospace;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.35);
`;

// ─── Component ────────────────────────────────────────────────────────────────

const AgencyTeamPanel = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [inviteEmail, setInviteEmail] = useState('');
  const [generating, setGenerating] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteExpiry, setInviteExpiry] = useState(null);
  const [copied, setCopied] = useState(false);
  const [genError, setGenError] = useState('');
  const [genOk, setGenOk] = useState('');

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agency/members`, {
        headers: { Authorization: `Bearer ${getAgencyToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLoginHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_URL}/api/agency/members/login-history`, {
        headers: { Authorization: `Bearer ${getAgencyToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLoginHistory(data.events || []);
      }
    } catch {
      // non-critical
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchLoginHistory();
  }, [fetchMembers, fetchLoginHistory]);

  const handleGenerateInvite = async (e) => {
    e.preventDefault();
    setGenError('');
    setGenOk('');
    setInviteLink(null);
    if (!inviteEmail || !inviteEmail.includes('@')) {
      setGenError('Enter a valid email address.');
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/agency/members/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAgencyToken()}`,
        },
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setInviteLink(data.invite_url);
      setInviteExpiry(data.expires_at ? new Date(data.expires_at).toLocaleDateString() : null);
      setGenOk(`Invite link created for ${inviteEmail.trim()}`);
      setInviteEmail('');
      fetchMembers();
    } catch (err) {
      setGenError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const isOwnerRole = (role) => ['owner', 'agencyowner'].includes(role);

  return (
    <Wrap>
      <Title>Team</Title>

      <Section>
        <SectionTitle>Current members</SectionTitle>
        {loading ? (
          <Empty>Loading…</Empty>
        ) : members.length === 0 ? (
          <Empty>No members yet.</Empty>
        ) : (
          <MemberTable>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Last login</Th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.email}>
                  <Td>{m.email}</Td>
                  <Td>
                    <RoleBadge $owner={isOwnerRole(m.role)}>
                      {isOwnerRole(m.role) ? 'Owner' : 'Member'}
                    </RoleBadge>
                  </Td>
                  <Td>
                    <StatusDot $active={m.status === 'active'} />
                    {m.status === 'active' ? 'Active' : 'Invite pending'}
                  </Td>
                  <Td style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {m.last_login_at
                      ? new Date(m.last_login_at).toLocaleDateString()
                      : '—'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </MemberTable>
        )}
      </Section>

      <Section>
        <SectionTitle>Invite a team member</SectionTitle>
        <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)', margin: '0 0 16px', lineHeight: 1.5 }}>
          Team members can upload and manage ads but cannot edit agency settings, invite others, or manage users.
        </p>
        <form onSubmit={handleGenerateInvite} noValidate>
          <InviteRow>
            <EmailInput
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="colleague@company.com"
            />
            <GenBtn type="submit" disabled={generating || !inviteEmail}>
              {generating ? 'Generating…' : 'Generate invite link'}
            </GenBtn>
          </InviteRow>
          {genError && <ErrMsg>{genError}</ErrMsg>}
          {genOk && <OkMsg>{genOk}</OkMsg>}
        </form>

        {inviteLink && (
          <>
            <LinkBox>
              <LinkText>{inviteLink}</LinkText>
              <CopyBtn onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy link'}
              </CopyBtn>
            </LinkBox>
            {inviteExpiry && (
              <Expiry>This link expires on {inviteExpiry}. Send it directly to your teammate — it can only be used once.</Expiry>
            )}
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>Login history</SectionTitle>
        {loadingHistory ? (
          <Empty>Loading…</Empty>
        ) : loginHistory.length === 0 ? (
          <Empty>No login events recorded yet.</Empty>
        ) : (
          <HistoryTable>
            <thead>
              <tr>
                <HistoryTh>Time</HistoryTh>
                <HistoryTh>User</HistoryTh>
                <HistoryTh>IP address</HistoryTh>
              </tr>
            </thead>
            <tbody>
              {loginHistory.map((ev, i) => (
                <tr key={i}>
                  <HistoryTd style={{ whiteSpace: 'nowrap' }}>
                    {ev.login_at
                      ? new Date(ev.login_at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })
                      : '—'}
                  </HistoryTd>
                  <HistoryTd>{ev.email}</HistoryTd>
                  <HistoryTd><IpText>{ev.ip || '—'}</IpText></HistoryTd>
                </tr>
              ))}
            </tbody>
          </HistoryTable>
        )}
      </Section>
    </Wrap>
  );
};

export default AgencyTeamPanel;
